import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { InputNumber } from 'primeng/inputnumber';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ControlIdentity, CplaceSingleNumberControlChange, CplaceSingleNumberControlParameters } from '../../../api/model/models';
import { CplaceControlWithEditModeComponent, CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR } from '../shared';
import { CplaceControlService } from '../shared/cplace-control.service';

@Component({
  selector: 'cf-frontend-sdk-cplace-single-number-control',
  templateUrl: './cplace-single-number-control.component.html',
  styleUrls: ['./styles/cplace-single-number-control.component.scss'],
  providers: [CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR(CplaceSingleNumberControlComponent)],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CplaceSingleNumberControlComponent extends CplaceControlWithEditModeComponent<number> {
  public static readonly componentName = 'CplaceSingleNumberControlComponent';
  public static readonly MAX_FRACTION_DIGITS = 20;

  @ViewChild('cplaceSingleNumberControlInput', { static: true, read: InputNumber })
  private numberInputComponent?: InputNumber;

  @HostBinding('class.cf-frontend-sdk-cplace-single-number-control') public cplaceSingleNumberControl = true;

  /**
   * Fractional digits of the control value
   * <= -1 Show number as is
   * = 0 Show as integer
   * >= 1 Show exactly that many fractional digits
   */
  @Input() public set fractionalDigits(val: number) {
    this._fractionalDigits = val;
    this.setMaxFractionDigits();
  }
  public get fractionalDigits(): number {
    return this._fractionalDigits;
  }
  private _fractionalDigits = 0;

  /**
   * Max number of fraction digits shown in the input.
   */
  public maxFractionDigits = CplaceSingleNumberControlComponent.MAX_FRACTION_DIGITS;

  /**
   * Min number of fraction digits shown in the input.
   */
  public minFractionDigits = 0;

  /**
   * Whether the empty value view should be shown.
   */
  public $showEmptyValue: Observable<boolean> = combineLatest([this.$value, this.$editMode]).pipe(
    map(([value, editMode]) => {
      return !value && !editMode;
    })
  );

  constructor(
    public elementRef: ElementRef,
    public changeDetectorRef: ChangeDetectorRef,
    public cplaceControlService: CplaceControlService
  ) {
    super(elementRef, changeDetectorRef, cplaceControlService);
  }

  private setMaxFractionDigits(): void {
    if (this.fractionalDigits <= -1) {
      this.maxFractionDigits = CplaceSingleNumberControlComponent.MAX_FRACTION_DIGITS;
      this.minFractionDigits = 0;
    } else if (this.fractionalDigits > CplaceSingleNumberControlComponent.MAX_FRACTION_DIGITS) {
      this.maxFractionDigits = CplaceSingleNumberControlComponent.MAX_FRACTION_DIGITS;
      this.minFractionDigits = CplaceSingleNumberControlComponent.MAX_FRACTION_DIGITS;
    } else {
      const digits = Math.trunc(this.fractionalDigits);
      this.maxFractionDigits = digits;
      this.minFractionDigits = digits;
    }
  }

  protected initializeControlParameters(controlParameters: CplaceSingleNumberControlParameters): void {
    this.value = controlParameters.value;
  }

  protected beforeSubmitHook(): void {
    this.numberInputComponent?.input.nativeElement.blur();
  }

  protected onCancelHook(): void {
    this.numberInputComponent?.input.nativeElement.blur();
  }

  protected getControlChange(identity: ControlIdentity, value: number | undefined): CplaceSingleNumberControlChange {
    return { featureName: identity.featureName, componentName: CplaceSingleNumberControlComponent.componentName, value: value };
  }

  protected getComponentClassName(): string {
    return CplaceSingleNumberControlComponent.componentName;
  }

  protected onClear(): void {
    this.value = undefined;
  }
}
